// Sovereign Emerald palette
const SE = {
  title:         '#0B3B2E',
  brand:         '#339274',
  champagne:     '#C9A961',
  champagneRing: 'rgba(201,169,97,0.45)',
  hairline:      'rgba(11,59,46,0.14)',
  labelGold:     '#A08A55',
  amber:         '#B45309',
  muted:         '#7C8A82',
};

export interface StatItem {
  label: string;
  value: string;
  hint?: string;
  tone?: 'default' | 'positive' | 'warning' | 'gold';
  /** Renders a status dot + smaller value (for word values like "Active"). */
  dot?: boolean;
}

const toneColor = (tone?: StatItem['tone']) =>
  tone === 'positive' ? SE.brand :
  tone === 'warning'  ? SE.amber :
  tone === 'gold'     ? SE.champagne :
  SE.title;

/** Boxless engraved KPI band: champagne hairlines above/below, vertical rules between stats. */
export default function StatBand({ stats }: { stats: StatItem[] }) {
  const goldRule = `linear-gradient(to right, transparent, ${SE.champagneRing}, transparent)`;
  return (
    <div className="relative">
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: goldRule }} />
      <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background: goldRule }} />
      <div className="grid grid-cols-2 lg:grid-cols-4">
        {stats.map((s, i) => (
          <div
            key={s.label}
            className={[
              'min-w-0 px-4 py-5 sm:px-6',
              i > 0 ? 'lg:border-l' : '',
              i % 2 === 1 ? 'max-lg:border-l' : '',
              i >= 2 ? 'max-lg:border-t' : '',
            ].join(' ')}
            style={{ borderColor: SE.hairline }}
          >
            <p className="text-[10.5px] font-bold uppercase tracking-[0.14em]" style={{ color: SE.labelGold }}>
              {s.label}
            </p>
            <div className="mt-2 flex items-center gap-2">
              {s.dot && (
                <span className="h-2 w-2 flex-shrink-0 rounded-full" style={{ backgroundColor: toneColor(s.tone) }} />
              )}
              <p
                className={`${s.dot ? 'text-[17px]' : 'text-[30px]'} font-bold leading-none tracking-tight`}
                style={{ color: toneColor(s.tone) }}
              >
                {s.value}
              </p>
            </div>
            {s.hint && (
              <p className="mt-2 truncate text-[11px] font-medium" style={{ color: SE.muted }} title={s.hint}>
                {s.hint}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
